import { useEffect, useRef, useState } from "react";
import type { ReactElement } from "react";
import { ApiError } from "@kundeninfo/api-client";
import type { TicketDetail, TicketLinkInstanzen } from "@kundeninfo/api-client";
import { Feld, Modal, ModalFuss, darfAbbrechen, useBestaetigung } from "@kundeninfo/ui";
import { Auswahl, Chip, Formular, Hinweis } from "@kundeninfo/ui/me-design";
import { KiKnopf, LoeschKnopf, Suchfeld } from "../../components/meHuellen.js";
import { SchreibHinweis, useSchreibweg } from "./useSchreibweg.js";
import { berlinerZeitpunkt } from "./ticketFormat.js";
import { REFERENCE_TEXT } from "./referenceTexts.js";
import type { TicketsClient } from "./ticketsClient.js";
import type { MilestonesClient } from "../milestones/milestonesClient.js";
import { TicketMilestones } from "../milestones/TicketMilestones.js";
import { HIERARCHY_TEXT } from "./hierarchyTexts.js";
import type { TicketDialogProps } from "./TicketDialog.js";

/**
 * REITER „VERKNÜPFUNGEN" (Spec „Arbeitsdialog › Verknüpfungen", F45): je Verknüpfung System und
 * Kennung, Stand von dort (Statusart als Chip, Titel), „gelesen vor … Min.", Öffnen im
 * Zielsystem in neuem Tab, Lösen nach gestapelter Bestätigung. Titel und Stand aus dem
 * Fremdsystem sind REINER Text (nie HTML); eine Adresse, die nicht https ist, wird nur als Text
 * gezeigt, nie als Link (Abwehr in der Tiefe, Review 21). Fail closed: Bei einem Abruffehler
 * bleibt der alte Stand stehen, daneben „Stand von <Zeit>, <Vermerk>".
 *
 * Hinzufügen im Panel (Hausregel Modal-Bearbeitung): EIN Feld „Adresse oder Kennung", dazu mit
 * eingerichteter Instanz die Auswahl „System" und die Suche im Zielsystem (Route mit Aufgabe 25).
 * Eine Adresse geht als `url` an den Server — er erkennt die Instanz oder legt einen Weblink
 * ohne Stand an; eine Kennung geht mit der gewählten Instanz. Befunde (422 `LINK_URL_UNGUELTIG`,
 * `LINK_INSTANZ_UNBEKANNT`) stehen am Feld; 409/403 über den gemeinsamen Schreibweg (B7).
 *
 * Abschnitt „Meilensteine“ (Spec D21, Aufgabe 8), nur mit `milestones`: `TicketMilestones` (views/milestones).
 */

type Link = TicketDetail["links"][number];
type Instanz = TicketLinkInstanzen["items"][number];
type Treffer = Awaited<ReturnType<TicketsClient["searchTicketLinks"]>>["items"][number];

const ART: Record<Link["remoteStateKind"], string> =
  { OPEN: "offen", IN_PROGRESS: "in Arbeit", CLOSED: "geschlossen", UNKNOWN: "unbekannt" };
const STATUS_ART: Record<Link["remoteStateKind"], "laeuft" | "aus" | "unbekannt"> =
  { OPEN: "laeuft", IN_PROGRESS: "laeuft", CLOSED: "aus", UNKNOWN: "unbekannt" };
/** Die Feldnamen der Befunde: `url`/`externalKey` am Feld „Adresse oder Kennung", `instance` an „System". */
const FELDER = ["url", "externalKey", "instance"] as const;
/** Entprellung der Suche im Zielsystem (jede Anfrage zählt gegen die Drossel, F27). */
const SUCHE_MS = 300;
/** Vermerk des Abgleichs bei abgelaufenem Budget (api/src/tickets/abgleich.ts). */
const STAND_AELTER = "Stand älter";

function istHttps(url: string | null): url is string {
  if (url === null) return false;
  try { return new URL(url).protocol === "https:"; } catch { return false; }
}

function name(l: Link): string {
  if (l.systemType !== "URL") return `${l.systemType === "GITLAB" ? "GitLab" : "YouTrack"} ${l.externalKey}`;
  let host = "";
  try { host = l.url === null ? "" : new URL(l.url).hostname; } catch { host = ""; }
  return host === "" ? "Link" : `Link ${host}`;
}

function standText(l: Link, jetzt: Date): string | null {
  if (l.systemType === "URL") return null;
  if (l.fetchError !== null) {
    const aelter = l.fetchError === STAND_AELTER;
    if (l.fetchedAt === null) return aelter ? "Noch nie gelesen" : `Noch nie gelesen, ${l.fetchError}`;
    return `Stand von ${berlinerZeitpunkt(l.fetchedAt)}, ${aelter ? "neuer Stand kam nicht rechtzeitig" : l.fetchError}`;
  }
  if (l.fetchedAt === null) return "Noch nicht gelesen";
  const minuten = Math.max(0, Math.round((jetzt.getTime() - new Date(l.fetchedAt).getTime()) / 60_000));
  return minuten < 60 ? `${ART[l.remoteStateKind]} · gelesen vor ${minuten} Min.`
    : `${ART[l.remoteStateKind]} · Stand von ${berlinerZeitpunkt(l.fetchedAt)}`;
}

const schluessel = (i: Instanz): string => `${i.system}|${i.key}`;

export function TicketVerknuepfungen(p: {
  d: TicketDetail; client: TicketsClient; onNeu: (d: TicketDetail) => void; jetzt?: () => Date;
  nachladen?: () => Promise<TicketDetail>;
  /** Abschnitt „Meilensteine“ (Aufgabe 8); ohne: kein Abschnitt (Akte unverändert). */
  milestones?: { client: MilestonesClient; onOpen: (m: { id: string; title: string }) => void };
}): ReactElement {
  /** `null` solange geladen wird; `"fehler"`, wenn das Laden scheiterte (Gesamtreview M-6). */
  const [instanzen, setInstanzen] = useState<Instanz[] | "fehler" | null>(null);
  const [hinzufuegen, setHinzufuegen] = useState(false);
  const [holt, setHolt] = useState(false);
  const weg = useSchreibweg(p.onNeu, p.nachladen === undefined ? {} : { nachladen: p.nachladen });
  const { bestaetigen } = useBestaetigung();
  const { client } = p;
  const ticketId = p.d.id;
  const companyId = p.d.companyId;
  const jetzt = (p.jetzt ?? (() => new Date()))();

  // Die Instanzen des Ticket-Unternehmens (B17) — ohne eine gibt es weder Auswahl noch Suche.
  // Reihenfolgewächter (Gesamtreview M-6): Wechselt das Unternehmen, gilt nur die Antwort des
  // letzten Ladens; bis sie da ist, steht „Verknüpfen" gesperrt — sonst öffnete das Panel ohne
  // Instanz und schickte eine Kennung als Adresse los. Ein Ladefehler ist kein „kein Fremdsystem".
  const ladung = useRef(0);
  useEffect(() => {
    const nummer = ++ladung.current;
    setInstanzen(null);
    client.listTicketLinkInstanzen(companyId)
      .then((r) => { if (ladung.current === nummer) setInstanzen(r.items); })
      .catch(() => { if (ladung.current === nummer) setInstanzen("fehler"); });
    return () => { if (ladung.current === nummer) ladung.current++; };
  }, [client, companyId]);
  const geladen = Array.isArray(instanzen) ? instanzen : [];

  async function loesen(l: Link): Promise<void> {
    if (!(await bestaetigen({ titel: "Verknüpfung lösen?", bestaetigenText: "Lösen",
                              text: `${REFERENCE_TEXT.unlinkText(name(l))} Im Zielsystem ändert sich nichts.` }))) return;
    await weg.schreiben(() => client.removeTicketLink(ticketId, l.id), "Die Verknüpfung ließ sich nicht lösen.");
  }

  async function aktualisieren(): Promise<void> {
    setHolt(true);
    try {
      await weg.schreiben(() => client.refreshTicketLinks(ticketId), "Der Stand ließ sich nicht holen.");
    } finally {
      setHolt(false);
    }
  }

  const mitStand = p.d.links.some((l) => l.systemType !== "URL");
  return (
    <div className="td__verknuepfungen">
      <div className="td__aktionszeile">
        <KiKnopf deaktiviert={weg.laeuft || instanzen === null} onClick={() => setHinzufuegen(true)}>Verknüpfen</KiKnopf>
        {mitStand && (
          <KiKnopf art="leise" deaktiviert={weg.laeuft} onClick={() => { void aktualisieren(); }}>
            {holt ? "Stand wird geholt …" : "Stand aktualisieren"}
          </KiKnopf>
        )}
      </div>
      {instanzen === "fehler" && (
        <Hinweis art="warnung">Instanzen konnten nicht geladen werden — Adressen werden als Link ohne Stand gespeichert.</Hinweis>
      )}
      {Array.isArray(instanzen) && instanzen.length === 0 && (
        <Hinweis art="info">Kein Fremdsystem angebunden — Adressen werden als Link ohne Stand gespeichert.</Hinweis>
      )}
      {p.d.links.length === 0 ? <p className="td__leer">Keine Verknüpfungen.</p> : (
        <ul className="td__liste">
          {p.d.links.map((l) => {
            const stand = standText(l, jetzt);
            const https = istHttps(l.url);
            return (
              <li key={l.id}>
                <span className="td__linkname">{name(l)}</span>
                {l.systemType !== "URL" && (
                  <Chip statusArt={STATUS_ART[l.remoteStateKind]}>{l.remoteState ?? ART[l.remoteStateKind]}</Chip>
                )}
                {l.remoteTitle !== null && <span className="td__linktitel">{l.remoteTitle}</span>}
                {stand !== null && <span className="td__neben">{stand}</span>}
                {l.remoteAssignee !== null && <span className="td__neben">{`Zuständig: ${l.remoteAssignee}`}</span>}
                {l.url !== null && (l.systemType === "URL" || !https) && <span className="td__neben td__adresse">{l.url}</span>}
                {https && <KiKnopf art="leise" name={`${name(l)} öffnen`} href={l.url!} ziel="_blank">Öffnen</KiKnopf>}
                <LoeschKnopf name={`${name(l)} lösen`} deaktiviert={weg.laeuft} onClick={() => { void loesen(l); }} />
              </li>
            );
          })}
        </ul>
      )}
      <SchreibHinweis meldung={weg.meldung} />
      {p.milestones !== undefined && (
        <TicketMilestones d={p.d} client={p.milestones.client} onOpen={p.milestones.onOpen} onNeu={p.onNeu}
          {...(p.nachladen === undefined ? {} : { nachladen: p.nachladen })} />
      )}
      {hinzufuegen && (
        <VerknuepfungHinzufuegen ticketId={ticketId} client={client} instanzen={geladen} onNeu={p.onNeu}
          {...(p.nachladen === undefined ? {} : { nachladen: p.nachladen })} onFertig={() => setHinzufuegen(false)} />
      )}
    </div>
  );
}

/**
 * Der Reiter „Verknüpfungen“ des Ticketdialogs samt Abschnitt „Meilensteine“ — EINE Fassung für Bereich Tickets, Kalender und
 * das Objektpanel des Tickets („Öffnen statt Springen“). `onMeilenstein` ist der Weg der Stelle zum Meilenstein-Panel (seitlich
 * über dem Dialog, dessen Ticket `d` ist).
 */
export function verknuepfungenReiter(client: TicketsClient,
  onMeilenstein: (d: TicketDetail, m: { id: string; title: string }) => void): NonNullable<TicketDialogProps["zusatzReiter"]>[number] {
  return { id: "verknuepfungen", label: HIERARCHY_TEXT.linksTab,
    inhalt: (d, neu, nachladen) => <TicketVerknuepfungen d={d} client={client} onNeu={neu} nachladen={nachladen}
      milestones={{ client, onOpen: (m) => onMeilenstein(d, m) }} /> };
}

/** Das Panel „Verknüpfung hinzufügen": eigener Schreibweg, damit Befunde IM Panel stehen. */
function VerknuepfungHinzufuegen(p: {
  ticketId: string; client: TicketsClient; instanzen: readonly Instanz[]; onNeu: (d: TicketDetail) => void;
  nachladen?: () => Promise<TicketDetail>; onFertig: () => void;
}): ReactElement {
  const [text, setText] = useState("");
  const [instanz, setInstanz] = useState(p.instanzen[0] === undefined ? "" : schluessel(p.instanzen[0]));
  const [suche, setSuche] = useState("");
  const [treffer, setTreffer] = useState<Treffer[]>([]);
  /** Leiser Hinweis unter der Suche, wenn das Zielsystem nicht antwortet (422) — eine leere
   *  Liste bedeutet dann nur noch „wirklich keine Treffer" (Nachlese P7/P4). */
  const [sucheHinweis, setSucheHinweis] = useState<string | null>(null);
  const weg = useSchreibweg(p.onNeu, { felder: FELDER, ...(p.nachladen === undefined ? {} : { nachladen: p.nachladen }) });
  const gewaehlt = p.instanzen.find((i) => schluessel(i) === instanz);
  const fehler = weg.feldFehler.url ?? weg.feldFehler.externalKey;
  const system = gewaehlt?.system;
  const key = gewaehlt?.key;
  const label = gewaehlt?.label;
  const { client } = p;
  const geaendert = text.trim() !== "" || suche.trim() !== "";

  const raeumen = () => { for (const f of FELDER) weg.feldRaeumen(f); };

  async function speichern(): Promise<void> {
    const t = text.trim();
    // Eine Adresse (mit Schema) entscheidet der Server; eine Kennung braucht die gewählte Instanz.
    const rumpf = gewaehlt === undefined || /^[a-z][a-z0-9+.-]*:\/\//i.test(t)
      ? { url: t } : { system: gewaehlt.system, instance: gewaehlt.key, externalKey: t };
    if (await weg.schreiben(() => p.client.addTicketLink(p.ticketId, rumpf), "Die Verknüpfung ließ sich nicht anlegen.")) {
      p.onFertig();
    }
  }
  async function abbrechen(): Promise<void> {
    if (weg.laeuft) return;
    if (await darfAbbrechen(geaendert)) p.onFertig();
  }
  // Suche entprellt (300 ms): erst wenn das Tippen ruht, und nur die Antwort zur letzten Eingabe zählt.
  useEffect(() => {
    const q = suche.trim();
    if (system === undefined || key === undefined || q.length < 2) { setTreffer([]); setSucheHinweis(null); return undefined; }
    let aktiv = true;
    const uhr = setTimeout(() => {
      client.searchTicketLinks(system, key, q)
        .then((r) => { if (aktiv) { setTreffer(r.items); setSucheHinweis(null); } })
        .catch((e: unknown) => {
          if (!aktiv) return;
          setTreffer([]);
          setSucheHinweis(e instanceof ApiError && e.code === "LINK_ZIEL_NICHT_ERREICHBAR"
            ? `${label ?? "Das Zielsystem"} ist gerade nicht erreichbar.` : "Die Suche ist gerade nicht möglich.");
        });
    }, SUCHE_MS);
    return () => { aktiv = false; clearTimeout(uhr); };
  }, [client, system, key, label, suche]);

  const mitInstanz = p.instanzen.length > 0;
  return (
    <Modal titel="Verknüpfung hinzufügen" breite="mittel" onAbbrechen={() => { void abbrechen(); }} abbrechenGesperrt={weg.laeuft}>
      <Formular beschriftung="oben">
        {mitInstanz && (
          <Auswahl beschriftung="System" wert={instanz} gesperrt={weg.laeuft} fehler={weg.feldFehler.instance ?? ""}
            onGeaendert={(e) => { raeumen(); setInstanz(e.wert); setSuche(""); }}>
            {p.instanzen.map((i) => <option key={schluessel(i)} value={schluessel(i)}>{i.label}</option>)}
          </Auswahl>
        )}
        <Feld id="tv-adresse" label="Adresse oder Kennung" wert={text} bearbeitbar max={500} gesperrt={weg.laeuft}
          onChange={(w) => { raeumen(); setText(w); }} fehler={fehler}
          hilfe={mitInstanz ? "https-Adresse einfügen oder die Kennung im gewählten System angeben."
            : "Vollständige https-Adresse — sie wird als Link ohne Stand gespeichert."} />
      </Formular>
      {mitInstanz && (
        <>
          <Suchfeld name="Im Zielsystem suchen" platzhalter="Titel oder Nummer (mindestens 2 Zeichen)" wert={suche}
            gesperrt={weg.laeuft} onText={setSuche} />
          {sucheHinweis !== null && <p className="td__neben" role="status">{sucheHinweis}</p>}
          {treffer.length > 0 && (
            <ul className="td__liste">
              {treffer.map((t) => (
                <li key={t.externalKey}>
                  <KiKnopf art="leise" name={`${t.externalKey} übernehmen`} onClick={() => { raeumen(); setText(t.externalKey); }}>
                    {t.externalKey}
                  </KiKnopf>
                  <span className="td__linktitel">{t.titel}</span>
                  {t.stand !== null && <span className="td__neben">{t.stand}</span>}
                </li>
              ))}
            </ul>
          )}
        </>
      )}
      <SchreibHinweis meldung={weg.meldung} />
      <ModalFuss speichernText="Speichern" speichernGesperrt={text.trim() === "" || weg.laeuft} abbrechenGesperrt={weg.laeuft}
        onSpeichern={() => { void speichern(); }} onAbbrechen={() => { void abbrechen(); }} />
    </Modal>
  );
}

